import {
    SimplePresentation,
    SimplePresentationRunnerController
} from "../../core/winpresentation/SimplePresentationRunner";
import {HTH} from "../HTH";
import {gsap} from "gsap";
import {HTHXSplitSymbol} from "../symbols/HTHXSplitSymbol";
import {HTHSymbol} from "../symbols/HTHSymbol";
import {HTHEnhancerSymbol} from "../symbols/HTHEnhancerSymbol";
import {HTHGameConfig} from "../HTHGameConfig";
import {BLEND_MODES} from "pixi.js";
import {HTHHelper} from "../utils/HTHHelper";
import {HTHSafeSymbol} from "../symbols/HTHSafeSymbol";
import {HTHSound} from "../sound/HTHSound";

export class HTHXSplitPresentation implements SimplePresentation {
    private _toAbortPresentation: boolean;

    constructor() {
    }

    shouldRun(): boolean {
        return HTH.model.isXSplitPresent && !HTH.model.isGodModeMaxWin;
    }

    start(controller: SimplePresentationRunnerController): void {
        const tl: gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                HTH.screenInteractive.setInteractiveState(false);
                controller.setSkipCallback();
                controller.finish()
            }
        });
        let soundEffectSpeed: number = 1;
        this._toAbortPresentation = false;
        if (HTH.model.gameData?.betWayWins.length === 0) {
            HTH.screenInteractive.setInteractiveState(true);
            if (HTHGameConfig.isSkipped[0] && !HTHGameConfig.isSkipped[1]) {
                //console.log("already at 1st skip Xsplitpresentation");
                soundEffectSpeed = 2;
                tl.timeScale(2);
            } else if (HTHGameConfig.isSkipped[1]) {
                //console.log("already at 2nd skip Xsplitpresentation");
                soundEffectSpeed = 4;
                tl.timeScale(4);
                HTH.screenInteractive.setInteractiveState(false);
            }
        }

        HTH.screenInteractive.clickArea.on('pointerdown', () => {
            if (!HTHGameConfig.isSkipped[0] && !HTHGameConfig.isSkipped[1]) {
                //console.log("1st skip Xsplitpresentation");
                HTHGameConfig.isSkipped[0] = true;
                soundEffectSpeed = 2;
                tl.timeScale(2);
                this.playSoundSpeed(soundEffectSpeed);
            } else if (HTHGameConfig.isSkipped[0] && !HTHGameConfig.isSkipped[1]) {
                //console.log("2nd skip xsplitpresentation");
                HTHGameConfig.isSkipped[1] = true;
                soundEffectSpeed = 4;
                tl.timeScale(4);
                this.playSoundSpeed(soundEffectSpeed);
                HTH.screenInteractive.setInteractiveState(false);
            }
        });

        if (HTH.model.gameData?.betWayWins.length > 0) {
            controller.setSkipCallback(() => { //keeping in if condition because it was getting click before interactive screen
                tl.timeScale(2);
                this.playSoundSpeed(2);
                this._toAbortPresentation = true;
            });
        }

        const xSplitPositionsPerReel: number[][][] = [[], [], [], [], [], []];

        HTH.model.xSplitSymbolPositions.forEach((xSplitSymbolPosition) => {
            xSplitPositionsPerReel[xSplitSymbolPosition[0]].push(xSplitSymbolPosition);
        })

        xSplitPositionsPerReel.forEach((xSplitSymbolPositions, i) => {
            const perReelTl: gsap.core.Timeline = gsap.timeline();
            xSplitSymbolPositions.forEach((value: number[], j) => {
                let delay: number = 0;
                const xsTl: gsap.core.Timeline = gsap.timeline();
                const xSSym: HTHSymbol = HTH.reelArea.getSymbol([value[0], value[1]]);
                let scatterArray: HTHSymbol[] = [];

                for (let i = 0; i < xSplitSymbolPositions.length; i++) {
                    if (xSplitSymbolPositions[i][0] === value[0] && xSplitSymbolPositions[i][1] < value[1]) {
                        delay = 0.50;
                    }
                }

                for (let i: number = 0; i < 4; i++) {
                    const symToCheck: HTHSymbol = HTH.reelArea.getSymbol([value[0], i]);
                    if (symToCheck.symName === "SC" || (symToCheck.symName === "SA" && !HTH.model.isFreespin)) {
                        scatterArray.push(symToCheck);
                    }
                }
                const splitEffectUp: PIXI.Sprite = new PIXI.Sprite(HTH.assetLoader.getTextures("splitLight")[0]);
                const splitEffectDown: PIXI.Sprite = new PIXI.Sprite(HTH.assetLoader.getTextures("splitLight")[0]);

                splitEffectDown.anchor.set(0.5, 0.5);
                splitEffectUp.anchor.set(0.5, 0.5);
                splitEffectUp.name = "effectUP";
                splitEffectDown.name = "effectDown";
                HTHHelper.copyLocalPosition(splitEffectUp, xSSym);
                splitEffectUp.blendMode = BLEND_MODES.ADD;
                splitEffectDown.blendMode = BLEND_MODES.ADD;
                splitEffectDown.rotation = 3.15;
                HTHHelper.copyLocalPosition(splitEffectDown, xSSym);


                if (xSSym.symName !== "EB" && xSSym.symName !== "SA") {
                    perReelTl.add(() => {
                        xSSym.movetoParticularMaskedLayer(HTH.reelArea.allReels[value[0]].reelView.reelLayers.maskedEffectsLayer);
                    }, 0)
                }
                if (xSSym.symName === "SA") {
                    perReelTl.add(() => {
                        HTH.symbolOverlay.addChild(xSSym);
                        xSSym.position.x = xSSym.reelId * HTHGameConfig.symbolWidth
                    })
                }

                const highlightTl: gsap.core.Timeline = gsap.timeline();
                highlightTl.add(() => {
                    HTH.symbolAnimation.addChild(splitEffectUp, splitEffectDown);
                }, 0)
                for (let i: number = 0; i < 4; i++) {
                    const symToLight: HTHSymbol = HTH.reelArea.getSymbol([value[0], i]);
                    if (symToLight.symName !== "EB" && symToLight.symName !== "SA") {
                        if (i !== xSSym.id) {
                            highlightTl.add((<HTHSymbol>symToLight).addSplitHighlight(), ((Math.abs(i - value[1]) - 1) * 0.25) + 0.05);
                        }
                    } else if (symToLight.symName === "EB") {
                        if ((<HTHEnhancerSymbol>symToLight).isEnhancerOpen) {
                            if (i !== xSSym.id) {
                                highlightTl.add((<HTHEnhancerSymbol>symToLight).addSplitHighlight(), ((Math.abs(i - value[1]) - 1) * 0.25) + 0.05);
                            }
                        }
                    } else if (symToLight.symName === "SA" && HTH.model.mode.includes("FREESPIN")) {
                        if (i !== xSSym.id) {
                            highlightTl.add((<HTHSafeSymbol>symToLight).addSplitHighlight(), ((Math.abs(i - value[1]) - 1) * 0.25) + 0.05);
                        }
                    }
                }
                perReelTl.add(highlightTl, delay);
                if (xSSym.symName !== "EB" && xSSym.symName !== "SA") {
                    xsTl.add(xSSym.animationChange("split").totalDuration(0.5), 0);
                    xsTl.add(() => {
                        HTHSound.playTrack(HTHSound.XSPLIT_XSPLIT)?.setRate(soundEffectSpeed);
                    }, "<");
                    xsTl.add((<HTHXSplitSymbol>xSSym).makeBgvisible(), "<");
                    xsTl.add((<HTHXSplitSymbol>xSSym).animationChange("transition_to_wild").totalDuration(0.5));
                    xsTl.add(() => {
                        HTHSound.playTrack(HTHSound.XSPLIT_TO_WILD)?.setRate(soundEffectSpeed);
                    }, "<");
                } else {
                    if (xSSym.symName === "EB") {
                        (<HTHXSplitSymbol>(<HTHEnhancerSymbol>xSSym).revealedSymbol!).spine.position.y -= 1;
                    }
                    xsTl.add((<HTHXSplitSymbol>((<HTHSafeSymbol | HTHEnhancerSymbol>xSSym).revealedSymbol!)).animationChange("split").totalDuration(0.5), 0);
                    xsTl.add(() => {
                        HTHSound.playTrack(HTHSound.XSPLIT_XSPLIT)?.setRate(soundEffectSpeed);
                    }, "<");
                    xsTl.add((<HTHXSplitSymbol>((<HTHSafeSymbol | HTHEnhancerSymbol>xSSym).revealedSymbol!)).makeBgvisible(), "<");
                    xsTl.add((<HTHXSplitSymbol>((<HTHSafeSymbol | HTHEnhancerSymbol>xSSym).revealedSymbol!)).animationChange("transition_to_wild").totalDuration(0.5));
                    xsTl.add(() => {
                        HTHSound.playTrack(HTHSound.XSPLIT_TO_WILD)?.setRate(soundEffectSpeed);
                    }, "<");
                }

                xsTl.to(splitEffectUp, {
                    duration: 0.25 * (value[1] + 1),
                    y: splitEffectUp.y - (value[1] + 1) * HTHGameConfig.symbolHeight,
                    ease: "none"
                }, 0)
                xsTl.to(splitEffectDown, {
                    duration: 0.25 * Math.abs(4 - value[1]),
                    y: splitEffectDown.y + Math.abs(4 - value[1]) * HTHGameConfig.symbolHeight,
                    ease: "none"

                }, 0);
                xsTl.add(() => {
                    HTH.symbolAnimation.removeChild(splitEffectUp, splitEffectDown);
                });
                if (xSSym.symName === "SA") {
                    xsTl.add(() => {
                        (<HTHSafeSymbol>xSSym).movetoParticularMaskedLayer(HTH.reelArea.allReels[xSSym.reelId].reelView.reelLayers.safeLayer);
                        xSSym.position.x = 0;
                    })
                }
                perReelTl.add(xsTl, delay);

                const splitTl: gsap.core.Timeline = gsap.timeline();
                for (let i: number = 0; i < 4; i++) {
                    const symToSplit: HTHSymbol = HTH.reelArea.getSymbol([value[0], i]);
                    if (i !== value[1]) {
                        if (symToSplit.symName !== "EB" && symToSplit.symName !== "SA") {
                            splitTl.add(symToSplit.addSplitEffect(symToSplit.symSize).totalDuration(0.4), ((Math.abs(i - value[1]) - 1) * 0.25) + 0.05);
                        } else if (symToSplit.symName === "EB") {
                            if ((<HTHEnhancerSymbol>symToSplit).isEnhancerOpen) {
                                splitTl.add(symToSplit.addSplitEffect((<HTHEnhancerSymbol>symToSplit).revealedSymSize).totalDuration(0.4), ((Math.abs(i - value[1]) - 1) * 0.25) + 0.05);
                            }
                        } else if (symToSplit.symName === "SA" && HTH.model.mode.includes("FREESPIN")) {
                            splitTl.add(symToSplit.addSplitEffect((<HTHSafeSymbol>symToSplit).safeSymSize).totalDuration(0.4), ((Math.abs(i - value[1]) - 1) * 0.25) + 0.05);
                        }
                    }
                }
                perReelTl.add(splitTl, delay);
            })

            xSplitSymbolPositions.forEach((value: number[]) => {
                const conversionToWildTl: gsap.core.Timeline = gsap.timeline();
                const xSplitSym: HTHSymbol = HTH.reelArea.getSymbol([value[0], value[1]]);

                if (xSplitSym.symName !== "EB" && xSplitSym.symName !== "SA") {
                    conversionToWildTl.add(HTH.reelArea.allReels[xSplitSym.reelId].reelView.switchSymbol(xSplitSym, "W"));
                } else {
                    if (xSplitSym.symName === "EB") {
                        conversionToWildTl.add(() => {
                            (<HTHEnhancerSymbol>xSplitSym).revealSymbol("W", xSplitSym.symSize);
                            HTH.reelArea.ebSpinArea.changeSymbol(xSplitSym.reelId, "W", 1);
                        });
                    } else if (xSplitSym.symName === "SA") {
                        conversionToWildTl.add(() => {
                            (<HTHSafeSymbol>xSplitSym).revealSymbol("W", xSplitSym.symSize, true);
                        });
                    }
                }
                perReelTl.add(conversionToWildTl);
            });
            perReelTl.add(() => {
                for (let i = 0; i < 6; i++) {
                    HTH.reelArea.allReels[i].reelView.reelLayers.maskedEffectsLayer.removeChildren();
                }
            });

            tl.add(perReelTl);
        })


    }

    private playSoundSpeed(chosenSpeed: number): void {
        if (HTHSound.soundPlayer.fetchSoundPlayer(HTHSound.XSPLIT_XSPLIT)?.isPlaying) {
            HTHSound.soundPlayer.fetchSoundPlayer(HTHSound.XSPLIT_XSPLIT)?.setRate(chosenSpeed);
        }
        if (HTHSound.soundPlayer.fetchSoundPlayer(HTHSound.XSPLIT_TO_WILD)?.isPlaying) {
            HTHSound.soundPlayer.fetchSoundPlayer(HTHSound.XSPLIT_TO_WILD)?.setRate(chosenSpeed);
        }
    }


}